import { ColorValue, ValueMappingEntry } from '../types/display';

export interface ValueMappingResult {
	displayText?: string;
	iconName?: string;
	color?: ColorValue;
}

/**
 * Evaluates value mapping to get display text, icon name, and/or color
 * @param mappings Array of value mapping entries
 * @param value The value to map
 * @returns Object containing optional displayText, iconName, and color
 */
export function evaluateValueMapping(
	mappings: ValueMappingEntry[] | undefined,
	value: any,
): ValueMappingResult {
	if (
		!mappings ||
		mappings.length === 0 ||
		value === null ||
		value === undefined
	) {
		return {};
	}

	// Convert value to string for comparison (case-insensitive)
	const stringValue = String(value).toLowerCase().trim();

	// Find matching mapping
	const mapping = mappings.find(
		(m) => String(m.value).toLowerCase().trim() === stringValue,
	);

	if (!mapping) {
		return {};
	}

	// F40: accept `displayValue` and `label` as aliases for `displayText`.
	// The ValueMappingEntry type lists all three as valid (displayText
	// is the primary; displayValue is documented as the badge/chip
	// alternative; label as a generic alias) but historically the
	// engine only read displayText, silently dropping any mapping
	// written against the aliases. Pick the first non-undefined.
	const resolvedDisplay =
		mapping.displayText ?? mapping.displayValue ?? mapping.label;

	return {
		...(resolvedDisplay !== undefined ? { displayText: resolvedDisplay } : {}),
		...(mapping.iconName !== undefined ? { iconName: mapping.iconName } : {}),
		...(mapping.color !== undefined ? { color: mapping.color } : {}),
	};
}

/**
 * Validates that value mappings are properly formatted
 * @param mappings Array of value mapping entries
 * @returns Array of error messages (empty if valid)
 */
export function validateValueMappings(mappings: ValueMappingEntry[]): string[] {
	const errors: string[] = [];
	const seenValues = new Set<string>();

	mappings.forEach((mapping, index) => {
		// Check for empty value (boolean values are never "empty"; matching
		// normalizes through String() the same way evaluateValueMapping does)
		if (
			!mapping.value ||
			(typeof mapping.value === 'string' && mapping.value.trim() === '')
		) {
			errors.push(`Mapping ${index + 1}: Original value cannot be empty`);
		}

		// Check for duplicate values
		const normalizedValue = String(mapping.value).toLowerCase().trim();
		if (seenValues.has(normalizedValue)) {
			errors.push(`Mapping ${index + 1}: Duplicate value "${mapping.value}"`);
		}
		seenValues.add(normalizedValue);

		// Validate colors if provided
		if (mapping.color) {
			if (mapping.color.bg && !isValidColor(mapping.color.bg)) {
				errors.push(`Mapping ${index + 1}: Invalid background color`);
			}
			if (mapping.color.text && !isValidColor(mapping.color.text)) {
				errors.push(`Mapping ${index + 1}: Invalid text color`);
			}
		}
	});

	return errors;
}

/**
 * Checks if a color string is valid (hex, rgb, rgba, or named color)
 */
function isValidColor(color: string): boolean {
	// Hex color
	if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
		return true;
	}

	// RGB/RGBA
	if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color)) {
		return true;
	}

	// Named colors (basic set)
	const namedColors = [
		'black',
		'white',
		'red',
		'green',
		'blue',
		'yellow',
		'cyan',
		'magenta',
		'gray',
		'grey',
		'orange',
		'purple',
		'brown',
		'pink',
		'transparent',
	];

	return namedColors.includes(color.toLowerCase());
}

/**
 * Applies value mapping to get the final display value
 * @param value Original value
 * @param mappings Value mappings
 * @returns Display value (mapped text or original value)
 */
export function getMappedDisplayValue(
	value: any,
	mappings: ValueMappingEntry[] | undefined,
): any {
	const mapping = evaluateValueMapping(mappings, value);
	return mapping.displayText !== undefined ? mapping.displayText : value;
}

/**
 * Gets the color for a value from mapping or returns undefined
 * @param value The value to get color for
 * @param mappings Value mappings
 * @returns Color value or undefined
 */
export function getMappedColor(
	value: any,
	mappings: ValueMappingEntry[] | undefined,
): ColorValue | undefined {
	const mapping = evaluateValueMapping(mappings, value);
	return mapping.color;
}

/**
 * Gets the icon name for a value from mapping or returns undefined
 * @param value The value to get icon for
 * @param mappings Value mappings
 * @returns Icon name or undefined
 */
export function getMappedIconName(
	value: any,
	mappings: ValueMappingEntry[] | undefined,
): string | undefined {
	const mapping = evaluateValueMapping(mappings, value);
	return mapping.iconName;
}
